import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { KEY_WORD_AUTHENTICATED_USER } from '@core/constants';
import { fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { safeJsonParse } from '@core/utils/safe-json-parse';

@Injectable({ providedIn: 'root' })
export class TabSyncService {
  private readonly TAB_ID = `tab_${Date.now()}_${Math.random().toString(36).substring(7)}`;
  private readonly MASTER_TAB_KEY = 'FLUX_MASTER_TAB';
  private readonly HEARTBEAT_INTERVAL = 5000;
  private heartbeatTimer?: number;

  constructor(private authService: AuthService) {
    this.setupTabSync();
    this.becomeMasterIfNeeded();
    this.startHeartbeat();
  }

  private setupTabSync(): void {
    fromEvent<StorageEvent>(window, 'storage')
      .pipe(takeUntilDestroyed())
      .subscribe((event) => {
        if (event.key === KEY_WORD_AUTHENTICATED_USER) {
          this.handleAuthChange(event);
        } else if (event.key === this.MASTER_TAB_KEY) {
          this.handleMasterTabChange();
        }
      });
  }

  private handleAuthChange(event: StorageEvent): void {
    const currentUser = this.authService.authenticatedUser();

    if (!event.newValue && currentUser) {
      this.authService.logout();
    }

    if (event.newValue && !currentUser) {
      window.location.reload();
    }
  }

  private becomeMasterIfNeeded(): void {
    const masterTab = localStorage.getItem(this.MASTER_TAB_KEY);

    if (masterTab) return;

    this.becomeMaster();
  }

  private becomeMaster(): void {
    localStorage.setItem(
      this.MASTER_TAB_KEY,
      JSON.stringify({
        tabId: this.TAB_ID,
        timestamp: Date.now(),
      }),
    );
  }

  private startHeartbeat(): void {
    this.heartbeatTimer = window.setInterval(() => {
      const masterTab = localStorage.getItem(this.MASTER_TAB_KEY);
      if (masterTab) {
        const parsed = safeJsonParse<{ tabId: string; timestamp: number }>(masterTab);

        if (!parsed) {
          this.becomeMaster();
          return;
        }

        if (parsed.tabId === this.TAB_ID) {
          this.becomeMaster();
        } else if (Date.now() - parsed.timestamp > this.HEARTBEAT_INTERVAL * 2) {
          this.becomeMaster();
        }
      } else {
        this.becomeMaster();
      }
    }, this.HEARTBEAT_INTERVAL);
  }

  private handleMasterTabChange(): void {}

  public cleanup(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }

    const masterTab = localStorage.getItem(this.MASTER_TAB_KEY);
    if (masterTab) {
      const parsed = JSON.parse(masterTab);
      if (parsed.tabId === this.TAB_ID) {
        localStorage.removeItem(this.MASTER_TAB_KEY);
      }
    }
  }
}
